import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    ArrowLeft,
    Bike,
    ShoppingBag,
    Banknote,
    Building2,
    Upload,
    ChefHat,
    CheckCircle2,
    Utensils,
} from "lucide-react";
import { useCart } from "@/context/CartContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import api, { fmtMVR } from "@/lib/api";
import { toast } from "sonner";

const PAYMENT_OPTIONS = {
    delivery: [
        {
            id: "cod",
            label: "Cash on Delivery",
            icon: Banknote,
            desc: "Pay our rider when your order arrives",
        },
        {
            id: "bank_transfer",
            label: "Bank Transfer (BML)",
            icon: Building2,
            desc: "Transfer & upload screenshot",
        },
    ],
    takeaway: [
        {
            id: "pickup",
            label: "Pay on Pickup",
            icon: Banknote,
            desc: "Pay at the counter when you collect",
        },
        {
            id: "bank_transfer",
            label: "Bank Transfer (BML)",
            icon: Building2,
            desc: "Transfer & upload screenshot",
        },
    ],
    dine_in: [
        {
            id: "pickup",
            label: "Pay at Counter",
            icon: Banknote,
            desc: "Pay at the counter when you dine in",
        },
        {
            id: "bank_transfer",
            label: "Bank Transfer (BML)",
            icon: Building2,
            desc: "Transfer & upload screenshot",
        },
    ],
};

export default function CheckoutPage() {
    const { items, total, clearCart } = useCart();
    const navigate = useNavigate();

    const [orderType, setOrderType] = useState("delivery");
    const [paymentMethod, setPaymentMethod] = useState("cod");
    const [name, setName] = useState("");
    const [phone, setPhone] = useState("+960");
    const [address, setAddress] = useState("");
    const [notes, setNotes] = useState("");
    const [screenshot, setScreenshot] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");

    const cleanPhone = phone.replace(/\s/g, "");
    const phoneValid = /^\+960\d{7}$/.test(cleanPhone);

    const serviceCharge = orderType === "dine_in" ? total * 0.1 : 0;
    const finalTotal = total + serviceCharge;

    const getErrorMessage = (err) => {
        const detail = err?.response?.data?.detail;

        if (typeof detail === "string") {
            return detail;
        }

        if (Array.isArray(detail)) {
            return detail
                .map((item) => item?.msg || JSON.stringify(item))
                .join(", ");
        }

        if (detail && typeof detail === "object") {
            return JSON.stringify(detail);
        }

        if (err?.message) {
            return err.message;
        }

        return "Order failed. Please check your details and try again.";
    };

    if (items.length === 0) {
        return (
            <div className="min-h-screen bg-pz-bg grid place-items-center px-6">
                <div className="text-center">
                    <h2 className="font-display text-4xl text-pz-text mb-3">
                        Your cart is empty
                    </h2>
                    <p className="text-pz-textMuted mb-6">
                        Add some delicious food first.
                    </p>
                    <Button
                        onClick={() => navigate("/menu")}
                        data-testid="empty-back-to-menu-btn"
                        className="bg-pz-primary hover:bg-pz-primaryHover rounded-full px-6"
                    >
                        Back to Menu
                    </Button>
                </div>
            </div>
        );
    }

    const handleOrderTypeChange = (type) => {
        setOrderType(type);

        if (type === "delivery") {
            setPaymentMethod("cod");
        } else {
            setPaymentMethod("pickup");
        }

        setScreenshot(null);
        setErrorMessage("");
    };

    const handlePhoneChange = (e) => {
        const rawValue = e.target.value;

        let digits = rawValue.replace(/\D/g, "");

        if (digits.startsWith("960")) {
            digits = digits.slice(3);
        }

        digits = digits.slice(0, 7);

        setPhone(`+960${digits}`);
        setErrorMessage("");
    };

    const handleUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        setUploading(true);
        setErrorMessage("");

        const fd = new FormData();
        fd.append("file", file);

        try {
            const res = await api.post("/upload/payment-screenshot", fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });

            setScreenshot(res.data);
            toast.success("Screenshot uploaded");
        } catch (err) {
            const message = getErrorMessage(err);
            setErrorMessage(message);
            toast.error(message);
        } finally {
            setUploading(false);
        }
    };

    const handleSubmit = async () => {
        setErrorMessage("");

        if (!name.trim()) {
            const msg = "Please enter your name";
            setErrorMessage(msg);
            return toast.error(msg);
        }

        if (!phoneValid) {
            const msg =
                "Phone must be +960 followed by 7 digits. Example: +9607777777";
            setErrorMessage(msg);
            return toast.error(msg);
        }

        if (orderType === "delivery" && !address.trim()) {
            const msg = "Address required for delivery";
            setErrorMessage(msg);
            return toast.error(msg);
        }

        if (!paymentMethod) {
            const msg = "Please select a payment method";
            setErrorMessage(msg);
            return toast.error(msg);
        }

        if (paymentMethod === "bank_transfer" && !screenshot) {
            const msg = "Please upload your payment screenshot";
            setErrorMessage(msg);
            return toast.error(msg);
        }

        setSubmitting(true);

        const orderItems = items.map((i) => ({
            id: i.id,
            name: i.name,
            price: Number(i.price),
            quantity: Number(i.quantity),
        }));

        if (orderType === "dine_in" && serviceCharge > 0) {
            orderItems.push({
                id: "service-charge-10",
                name: "10% Service Charge",
                price: Number(serviceCharge.toFixed(2)),
                quantity: 1,
            });
        }

        const payload = {
            customer_name: name.trim(),
            phone: cleanPhone,
            order_type: orderType,
            address: orderType === "delivery" ? address.trim() : null,
            items: orderItems,
            payment_method: paymentMethod,
            payment_screenshot: screenshot?.filename || null,
            notes: notes.trim() || null,
        };

        try {
            const res = await api.post("/orders", payload);

            const orderNumber = res.data.order_number;
            clearCart();
            navigate(`/confirmation/${orderNumber}`, { state: res.data });
        } catch (err) {
            const message = getErrorMessage(err);

            console.error("Order failed:", {
                message,
                status: err?.response?.status,
                backend: err?.response?.data,
                payload,
            });

            setErrorMessage(message);
            toast.error(message);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div
            className="min-h-screen bg-pz-bg paper-grain pb-24"
            data-testid="checkout-page"
        >
            <header className="sticky top-0 z-30 bg-pz-bg/95 backdrop-blur-xl border-b border-pz-border">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4 flex items-center gap-3">
                    <button
                        onClick={() => navigate("/menu")}
                        data-testid="checkout-back-btn"
                        className="text-pz-text hover:text-pz-primary"
                    >
                        <ArrowLeft size={22} />
                    </button>

                    <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-full bg-pz-primary text-white grid place-items-center">
                            <ChefHat size={16} />
                        </div>
                        <span className="font-display text-xl tracking-wide text-pz-text">
                            Checkout
                        </span>
                    </div>
                </div>
            </header>

            <main className="max-w-4xl mx-auto px-4 sm:px-6 py-8 grid lg:grid-cols-[1fr_360px] gap-8">
                <div className="space-y-7">
                    {errorMessage && (
                        <div
                            data-testid="checkout-error-message"
                            className="rounded-2xl border border-red-200 bg-red-50 px-5 py-4 text-sm font-semibold text-red-800"
                        >
                            {errorMessage}
                        </div>
                    )}

                    {/* Order Type */}
                    <section className="bg-white rounded-2xl border border-pz-border p-5 sm:p-6">
                        <h3 className="font-display text-2xl text-pz-text mb-1">
                            How would you like it?
                        </h3>
                        <p className="text-sm text-pz-textMuted mb-4">
                            Choose delivery, takeaway-pickup, or dine-in
                        </p>

                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                            {[
                                {
                                    id: "delivery",
                                    label: "Delivery",
                                    icon: Bike,
                                    desc: "We bring it to you",
                                },
                                {
                                    id: "takeaway",
                                    label: "Takeaway-Pickup",
                                    icon: ShoppingBag,
                                    desc: "Pickup at counter",
                                },
                                {
                                    id: "dine_in",
                                    label: "Dine-in",
                                    icon: Utensils,
                                    desc: "Eat at the restaurant",
                                },
                            ].map((opt) => (
                                <button
                                    key={opt.id}
                                    onClick={() =>
                                        handleOrderTypeChange(opt.id)
                                    }
                                    data-testid={`order-type-${opt.id}`}
                                    className={`text-left rounded-xl border p-4 transition-all ${
                                        orderType === opt.id
                                            ? "border-pz-primary bg-pz-primary/5 ring-2 ring-pz-primary/30"
                                            : "border-pz-border hover:border-pz-primary/40"
                                    }`}
                                >
                                    <opt.icon
                                        className={`mb-2 ${
                                            orderType === opt.id
                                                ? "text-pz-primary"
                                                : "text-pz-textMuted"
                                        }`}
                                        size={22}
                                    />
                                    <div className="font-semibold text-pz-text">
                                        {opt.label}
                                    </div>
                                    <div className="text-xs text-pz-textMuted">
                                        {opt.desc}
                                    </div>
                                </button>
                            ))}
                        </div>
                    </section>

                    {/* Customer Details */}
                    <section className="bg-white rounded-2xl border border-pz-border p-5 sm:p-6 space-y-4">
                        <h3 className="font-display text-2xl text-pz-text mb-1">
                            Your Details
                        </h3>

                        <div>
                            <Label className="text-pz-text">Full Name</Label>
                            <Input
                                value={name}
                                onChange={(e) => {
                                    setName(e.target.value);
                                    setErrorMessage("");
                                }}
                                data-testid="checkout-name-input"
                                placeholder="Ahmed Mohamed"
                                className="mt-1.5 rounded-xl border-pz-border focus-visible:ring-pz-primary"
                            />
                        </div>

                        <div>
                            <Label className="text-pz-text">
                                Phone Number{" "}
                                <span className="text-pz-textMuted text-xs">
                                    (Maldives only)
                                </span>
                            </Label>
                            <Input
                                value={phone}
                                onChange={handlePhoneChange}
                                data-testid="checkout-phone-input"
                                placeholder="+9607777777"
                                className="mt-1.5 rounded-xl border-pz-border focus-visible:ring-pz-primary"
                            />

                            {!phoneValid && phone.length > 4 && (
                                <p className="text-xs text-pz-primary mt-1">
                                    Format: +960 followed by 7 digits. Example:
                                    +9607777777
                                </p>
                            )}
                        </div>

                        {orderType === "delivery" && (
                            <div>
                                <Label className="text-pz-text">
                                    Delivery Address
                                </Label>
                                <Textarea
                                    value={address}
                                    onChange={(e) => {
                                        setAddress(e.target.value);
                                        setErrorMessage("");
                                    }}
                                    data-testid="checkout-address-input"
                                    placeholder="House name, street, building, floor — Malé"
                                    className="mt-1.5 rounded-xl border-pz-border focus-visible:ring-pz-primary min-h-[80px]"
                                />
                            </div>
                        )}

                        <div>
                            <Label className="text-pz-text">
                                Notes{" "}
                                <span className="text-pz-textMuted text-xs">
                                    (optional)
                                </span>
                            </Label>
                            <Textarea
                                value={notes}
                                onChange={(e) => {
                                    setNotes(e.target.value);
                                    setErrorMessage("");
                                }}
                                data-testid="checkout-notes-input"
                                placeholder="Extra spicy, no onions, table notes, etc."
                                className="mt-1.5 rounded-xl border-pz-border focus-visible:ring-pz-primary min-h-[60px]"
                            />
                        </div>
                    </section>

                    {/* Payment */}
                    <section className="bg-white rounded-2xl border border-pz-border p-5 sm:p-6">
                        <h3 className="font-display text-2xl text-pz-text mb-1">
                            Payment Method
                        </h3>
                        <p className="text-sm text-pz-textMuted mb-4">
                            Maldives-friendly options
                        </p>

                        <div className="space-y-3">
                            {PAYMENT_OPTIONS[orderType].map((opt) => (
                                <button
                                    key={opt.id}
                                    onClick={() => {
                                        setPaymentMethod(opt.id);
                                        setErrorMessage("");
                                    }}
                                    data-testid={`payment-${opt.id}`}
                                    className={`w-full flex items-center gap-4 text-left rounded-xl border p-4 transition-all ${
                                        paymentMethod === opt.id
                                            ? "border-pz-primary bg-pz-primary/5 ring-2 ring-pz-primary/30"
                                            : "border-pz-border hover:border-pz-primary/40"
                                    }`}
                                >
                                    <div
                                        className={`w-10 h-10 rounded-full grid place-items-center ${
                                            paymentMethod === opt.id
                                                ? "bg-pz-primary text-white"
                                                : "bg-pz-bg2 text-pz-textMuted"
                                        }`}
                                    >
                                        <opt.icon size={18} />
                                    </div>

                                    <div className="flex-1">
                                        <div className="font-semibold text-pz-text">
                                            {opt.label}
                                        </div>
                                        <div className="text-xs text-pz-textMuted">
                                            {opt.desc}
                                        </div>
                                    </div>

                                    {paymentMethod === opt.id && (
                                        <CheckCircle2
                                            className="text-pz-primary"
                                            size={20}
                                        />
                                    )}
                                </button>
                            ))}
                        </div>

                        {paymentMethod === "bank_transfer" && (
                            <div className="mt-5 p-4 rounded-xl bg-pz-bg2 border border-pz-border">
                                <h4 className="font-semibold text-pz-text mb-2 uppercase text-xs tracking-wider">
                                    BML Bank Transfer
                                </h4>

                                <div className="text-sm text-pz-textMuted space-y-1">
                                    <p>
                                        <span className="text-pz-text font-medium">
                                            Account Name:
                                        </span>{" "}
                                        Pizano
                                    </p>
                                    <p>
                                        <span className="text-pz-text font-medium">
                                            Account Number:
                                        </span>{" "}
                                        7730000728409
                                    </p>
                                    <p>
                                        <span className="text-pz-text font-medium">
                                            Bank:
                                        </span>{" "}
                                        BML
                                    </p>
                                    <p>
                                        <span className="text-pz-text font-medium">
                                            Reference:
                                        </span>{" "}
                                        Your phone number
                                    </p>
                                </div>

                                <div className="mt-4">
                                    <Label className="text-pz-text text-sm">
                                        Upload payment screenshot
                                    </Label>

                                    <label
                                        className="mt-2 flex items-center gap-3 p-3 rounded-xl border-2 border-dashed border-pz-border hover:border-pz-primary cursor-pointer transition-colors bg-white"
                                        data-testid="payment-screenshot-upload"
                                    >
                                        <Upload
                                            size={20}
                                            className="text-pz-primary"
                                        />
                                        <span className="text-sm text-pz-textMuted flex-1">
                                            {screenshot
                                                ? "Screenshot uploaded ✓"
                                                : uploading
                                                  ? "Uploading…"
                                                  : "Tap to choose image (JPG/PNG, max 8MB)"}
                                        </span>
                                        <input
                                            type="file"
                                            accept="image/*"
                                            onChange={handleUpload}
                                            className="hidden"
                                            data-testid="payment-screenshot-input"
                                        />
                                    </label>
                                </div>
                            </div>
                        )}
                    </section>
                </div>

                {/* Summary */}
                <aside className="lg:sticky lg:top-24 h-fit bg-white rounded-2xl border border-pz-border p-5 sm:p-6 space-y-4">
                    <h3 className="font-display text-2xl text-pz-text">
                        Order Summary
                    </h3>

                    <div className="space-y-2 max-h-72 overflow-y-auto">
                        {items.map((i) => (
                            <div
                                key={i.id}
                                className="flex justify-between text-sm"
                            >
                                <span className="text-pz-text">
                                    <span className="font-semibold">
                                        {i.quantity}×
                                    </span>{" "}
                                    {i.name}
                                </span>
                                <span className="text-pz-textMuted">
                                    {fmtMVR(i.price * i.quantity)}
                                </span>
                            </div>
                        ))}
                    </div>

                    <div className="border-t border-pz-border pt-4 space-y-2">
                        <div className="flex items-center justify-between">
                            <span className="text-pz-textMuted uppercase text-xs tracking-wider font-semibold">
                                Subtotal
                            </span>
                            <span className="text-pz-textMuted text-sm font-semibold">
                                {fmtMVR(total)}
                            </span>
                        </div>

                        {orderType === "dine_in" && (
                            <div className="flex items-center justify-between">
                                <span className="text-pz-textMuted uppercase text-xs tracking-wider font-semibold">
                                    Service Charge 10%
                                </span>
                                <span className="text-pz-textMuted text-sm font-semibold">
                                    {fmtMVR(serviceCharge)}
                                </span>
                            </div>
                        )}

                        <div className="flex items-center justify-between pt-3 border-t border-pz-border">
                            <span className="text-pz-textMuted uppercase text-xs tracking-wider font-semibold">
                                Total
                            </span>
                            <span
                                className="font-display text-3xl text-pz-primary"
                                data-testid="checkout-total"
                            >
                                {fmtMVR(finalTotal)}
                            </span>
                        </div>
                    </div>

                    <Button
                        onClick={handleSubmit}
                        disabled={submitting}
                        data-testid="place-order-btn"
                        className="w-full h-12 rounded-full bg-pz-primary hover:bg-pz-primaryHover text-white font-bold uppercase tracking-wider text-sm"
                    >
                        {submitting ? "Placing order..." : "Place Order"}
                    </Button>

                    <p className="text-xs text-pz-textMuted text-center">
                        By placing this order you agree to be contacted on the
                        phone number provided.
                    </p>
                </aside>
            </main>
        </div>
    );
}
